
import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { Trash2, Calendar, List, Clock, User, Phone, Plus, X, Info, ChevronLeft, ChevronRight, Mail, Edit2, AlertOctagon, Loader, MapPin, Check, Save, Tag, Search, Filter } from 'lucide-react';
import { API_BASE } from '../../config';

// --- Modal de création / modification de RDV ---
const BookingEditorModal = ({ isOpen, onClose, booking, onSave, loading, services, lang, initialData, t }) => {
    const [formData, setFormData] = useState({
        customer_name: '', email: '', phone: '', service_type: '', 
        booking_date: '', booking_time: '', address: '', notes: '', 
        status: 'pending', duration: 60
    });
    
    const [selectedServices, setSelectedServices] = useState([]);
    const [bookedSlots, setBookedSlots] = useState([]);
    const [viewDate, setViewDate] = useState(new Date());

    const getName = useCallback((s) => s[`name_${lang}`] || s.name_fr || s.name, [lang]);
    const bT = t?.admin?.bookings || {};
    const cT = t?.admin?.common || {};

    useEffect(() => {
        if (booking && isOpen) {
            setFormData({
                ...booking,
                booking_date: booking.booking_date || '',
                booking_time: booking.booking_time || '',
                status: booking.status || 'pending'
            });
            const currentServices = booking.service_type ? booking.service_type.split(' + ') : [];
            setSelectedServices(currentServices);
            if (booking.booking_date) setViewDate(new Date(booking.booking_date));
        } else if (initialData && isOpen) {
            setFormData({
                customer_name: '', email: '', phone: '', service_type: '', 
                booking_date: initialData.date || '', 
                booking_time: initialData.time || '10:00', 
                address: '', notes: '', status: 'confirmed', duration: 60
            });
            setSelectedServices([]);
            if (initialData.date) setViewDate(new Date(initialData.date));
        } else if (isOpen) {
            setFormData({
                customer_name: '', email: '', phone: '', service_type: '', 
                booking_date: new Date().toISOString().split('T')[0], 
                booking_time: '10:00', address: '', notes: '', status: 'pending', duration: 60
            });
            setSelectedServices([]);
            setViewDate(new Date());
        }
    }, [booking, isOpen, initialData]);

    useEffect(() => {
        if (formData.booking_date && isOpen) {
            fetch(`${API_BASE}/bookings/availability?date=${formData.booking_date}`)
                .then(res => res.json())
                .then(data => setBookedSlots(Array.isArray(data) ? data.map(t => String(t).substring(0, 5)) : []))
                .catch(err => console.error("Erreur disponibilité:", err));
        }
    }, [formData.booking_date, isOpen]);

    const calendarData = useMemo(() => {
        const year = viewDate.getFullYear(), month = viewDate.getMonth(), daysInMonth = new Date(year, month + 1, 0).getDate();
        const firstDay = new Date(year, month, 1).getDay(), adjustedFirstDay = firstDay === 0 ? 6 : firstDay - 1, days = [];
        for (let i = 0; i < adjustedFirstDay; i++) days.push(null);
        for (let i = 1; i <= daysInMonth; i++) {
            days.push({ day: i, dateStr: `${year}-${String(month + 1).padStart(2, '0')}-${String(i).padStart(2, '0')}` });
        }
        return days;
    }, [viewDate]);

    if (!isOpen) return null;

    const handleFormSubmit = (e) => {
        e.preventDefault();
        const serviceString = selectedServices.filter(s => s !== '').join(' + ');
        onSave({ ...formData, service_type: serviceString });
    };

    return (
        <div className="fixed inset-0 bg-slate-950/70 backdrop-blur-md z-[150] flex items-center justify-center p-4">
            <div className="bg-white rounded-[2.5rem] shadow-2xl w-full max-w-5xl overflow-hidden animate-fade-in-up max-h-[95vh] flex flex-col border border-white/20">
                <div className="p-6 border-b border-slate-100 flex justify-between items-center bg-slate-50/50">
                    <div className="flex items-center gap-3">
                        <div className="bg-violet-600 p-2 rounded-xl text-white">
                            {booking ? <Edit2 className="h-5 w-5"/> : <Plus className="h-5 w-5"/>}
                        </div>
                        <h3 className="text-xl font-black text-slate-800 uppercase tracking-tight">
                            {booking ? bT.modalTitleEdit : bT.modalTitleAdd}
                        </h3>
                    </div>
                    <button onClick={onClose} className="p-3 hover:bg-slate-200 rounded-full transition-all text-slate-400 hover:text-slate-600"><X className="h-6 w-6"/></button>
                </div>
                
                <form onSubmit={handleFormSubmit} className="p-6 md:p-10 space-y-8 overflow-y-auto custom-scrollbar bg-slate-50/30">
                    <div className="grid lg:grid-cols-3 gap-8">
                        <div className="lg:col-span-2 space-y-6">
                            <div className="bg-white p-6 rounded-3xl border border-slate-100 shadow-sm space-y-4">
                                <h4 className="text-[10px] font-black uppercase text-slate-400 flex items-center tracking-widest">
                                    <Tag className="h-4 w-4 mr-2 text-violet-600" /> {t.booking.steps.services}
                                </h4>
                                <div className="space-y-3">
                                    {selectedServices.map((cs, idx) => (
                                        <div key={idx} className="flex gap-2">
                                            <select required value={cs} onChange={(e) => { 
                                                const n = [...selectedServices]; 
                                                n[idx] = e.target.value; 
                                                setSelectedServices(n); 
                                            }} className="flex-1 p-4 border-2 border-slate-100 rounded-2xl bg-slate-50/50 focus:bg-white focus:border-violet-500 outline-none font-bold text-sm transition-all">
                                                <option value="">{t.booking.selectService}</option>
                                                {services.map(s => <option key={s.id} value={getName(s)}>{getName(s)} — {s.price}€</option>)}
                                            </select>
                                            <button type="button" onClick={() => setSelectedServices(selectedServices.filter((_, i) => i !== idx))} className="p-4 text-slate-300 hover:text-red-500 transition-all"><X className="h-5 w-5"/></button>
                                        </div>
                                    ))}
                                    <button type="button" onClick={() => setSelectedServices([...selectedServices, ''])} className="w-full py-4 border-2 border-dashed border-slate-200 rounded-2xl text-slate-400 font-bold text-xs hover:border-violet-300 hover:text-violet-600 transition-all flex items-center justify-center gap-2">
                                        <Plus className="h-4 w-4"/> {t.booking.addService}
                                    </button>
                                </div>
                            </div>

                            <div className="bg-white p-8 rounded-3xl border border-slate-100 shadow-sm space-y-6">
                                <h4 className="text-[10px] font-black uppercase text-slate-400 flex items-center tracking-widest">
                                    <User className="h-4 w-4 mr-2 text-violet-600" /> {t.booking.steps.personal}
                                </h4>
                                <div className="grid md:grid-cols-2 gap-6">
                                    <input required type="text" placeholder={t.booking.name} value={formData.customer_name} onChange={e=>setFormData({...formData, customer_name: e.target.value})} className="w-full p-4 bg-slate-50 border-2 border-transparent rounded-2xl focus:bg-white focus:border-violet-500 outline-none font-bold text-sm transition-all" />
                                    <input required type="email" placeholder={t.booking.email} value={formData.email} onChange={e=>setFormData({...formData, email: e.target.value})} className="w-full p-4 bg-slate-50 border-2 border-transparent rounded-2xl focus:bg-white focus:border-violet-500 outline-none font-bold text-sm transition-all" />
                                    <input type="tel" placeholder={t.booking.phone} value={formData.phone} onChange={e=>setFormData({...formData, phone: e.target.value})} className="w-full p-4 bg-slate-50 border-2 border-transparent rounded-2xl focus:bg-white focus:border-violet-500 outline-none font-bold text-sm transition-all" />
                                    <input required type="text" placeholder={t.booking.address} value={formData.address} onChange={e=>setFormData({...formData, address: e.target.value})} className="w-full p-4 bg-slate-50 border-2 border-transparent rounded-2xl focus:bg-white focus:border-violet-500 outline-none font-bold text-sm transition-all" />
                                </div>
                                <textarea placeholder={t.booking.placeholders.notes} value={formData.notes} onChange={e=>setFormData({...formData, notes: e.target.value})} className="w-full p-4 bg-slate-50 border-2 border-transparent rounded-2xl focus:bg-white focus:border-violet-500 outline-none font-bold text-sm transition-all resize-none h-24" />
                            </div>
                        </div>

                        <div className="space-y-6">
                            <div className="bg-white p-6 rounded-3xl border border-slate-100 shadow-sm">
                                <h4 className="text-[10px] font-black uppercase text-slate-400 mb-4 tracking-widest">{bT.date}</h4>
                                <div className="border border-slate-100 rounded-2xl overflow-hidden">
                                    <div className="flex justify-between p-3 bg-slate-50">
                                        <span className="font-bold text-xs capitalize">{viewDate.toLocaleDateString(lang, {month: 'short', year: 'numeric'})}</span>
                                        <div className="flex gap-1">
                                            <button type="button" onClick={() => setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth()-1, 1))} className="p-1 hover:bg-white rounded"><ChevronLeft className="h-4 w-4"/></button>
                                            <button type="button" onClick={() => setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth()+1, 1))} className="p-1 hover:bg-white rounded"><ChevronRight className="h-4 w-4"/></button>
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-7 p-2 text-center text-[10px]">
                                        {['L','M','M','J','V','S','D'].map(d=><div key={d} className="py-1 font-black text-slate-300">{d}</div>)}
                                        {calendarData.map((d,i)=>d ? (
                                            <button key={i} type="button" onClick={()=>setFormData({...formData, booking_date: d.dateStr})} className={`p-1.5 font-bold rounded-lg m-0.5 ${formData.booking_date===d.dateStr ? 'bg-violet-600 text-white' : 'hover:bg-slate-50 text-slate-600'}`}>
                                                {d.day}
                                            </button>
                                        ) : <div key={i}></div>)}
                                    </div>
                                </div>
                            </div>

                            <div className="bg-white p-6 rounded-3xl border border-slate-100 shadow-sm">
                                <h4 className="text-[10px] font-black uppercase text-slate-400 mb-4 tracking-widest">{bT.time} & {bT.status}</h4>
                                <div className="space-y-4">
                                    <input type="time" value={formData.booking_time} onChange={e=>setFormData({...formData, booking_time: e.target.value})} className="w-full p-4 bg-slate-50 border-2 border-slate-100 rounded-2xl font-black text-center focus:border-violet-500 outline-none" />
                                    <select value={formData.status} onChange={e=>setFormData({...formData, status: e.target.value})} className={`w-full p-4 rounded-2xl border-2 font-black text-sm outline-none transition-all ${
                                        formData.status === 'confirmed' ? 'bg-green-50 border-green-200 text-green-700' : 
                                        formData.status === 'pending' ? 'bg-amber-50 border-amber-200 text-amber-700' : 
                                        'bg-slate-50 border-slate-200 text-slate-600'
                                    }`}>
                                        <option value="pending">{bT.pending}</option>
                                        <option value="confirmed">{bT.confirmed}</option>
                                        <option value="cancelled">Annulé</option>
                                        <option value="no-show">No-Show</option>
                                    </select>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="pt-4 flex gap-4">
                        <button type="button" onClick={onClose} className="flex-1 py-5 bg-slate-100 border-2 border-slate-200 text-slate-600 rounded-3xl font-black uppercase text-[10px] hover:bg-slate-200 transition-all tracking-widest">{cT.abandon}</button>
                        <button type="submit" disabled={loading || selectedServices.filter(s => s !== '').length === 0 || !formData.booking_date}
						className="flex-[2] py-5 bg-violet-600 text-white rounded-3xl font-black uppercase text-xs hover:bg-violet-700 transition-all shadow-xl flex justify-center items-center gap-3 disabled:opacity-50 tracking-widest" >
						{loading ? <Loader className="animate-spin h-6 w-6"/> : <><Save className="h-6 w-6"/> {cT.save}</>} 
						</button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default function AdminBookings({ bookings, onDelete, onRefresh, t, lang }) {
  const [viewMode, setViewMode] = useState('week'); 
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [editingBooking, setEditingBooking] = useState(null);
  const [quickCreateData, setQuickCreateData] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [services, setServices] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  
  const calendarRef = useRef(null);
  const adminT = t?.admin?.common || {};
  const bookT = t?.admin?.bookings || {};

  useEffect(() => {
      fetch(`${API_BASE}/services`).then(res => res.json()).then(data => setServices(data || []));
  }, []);

  const weekDates = useMemo(() => {
    const d = new Date(selectedDate);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1);
    const monday = new Date(d.setDate(diff));
    return Array.from({ length: 7 }, (_, i) => {
        const date = new Date(monday);
        date.setDate(monday.getDate() + i);
        return date.toISOString().split('T')[0];
    });
  }, [selectedDate]);

  const currentViewBookings = useMemo(() => {
    if (viewMode === 'day') return bookings.filter(b => b.booking_date === selectedDate);
    if (viewMode === 'week') return bookings.filter(b => weekDates.includes(b.booking_date));
    if (viewMode === 'month') {
        const month = new Date(selectedDate).getMonth();
        const year = new Date(selectedDate).getFullYear();
        return bookings.filter(b => {
            const bd = new Date(b.booking_date);
            return bd.getMonth() === month && bd.getFullYear() === year;
        });
    }
    if (viewMode === 'list') {
        return bookings.filter(b => 
            (b.customer_name?.toLowerCase().includes(searchTerm.toLowerCase())) ||
            (b.email?.toLowerCase().includes(searchTerm.toLowerCase())) ||
            (b.service_type?.toLowerCase().includes(searchTerm.toLowerCase()))
        );
    }
    return bookings;
  }, [bookings, viewMode, selectedDate, weekDates, searchTerm]);

  const handleNavigate = (direction) => {
      const d = new Date(selectedDate);
      if (viewMode === 'day') d.setDate(d.getDate() + direction);
      else if (viewMode === 'week') d.setDate(d.getDate() + (direction * 7));
      else if (viewMode === 'month') d.setMonth(d.getMonth() + direction);
      setSelectedDate(d.toISOString().split('T')[0]);
  };

  const handleSaveBooking = async (data) => {
      setActionLoading(true);
      try {
          const method = data.id ? 'PUT' : 'POST';
          const url = data.id ? `${API_BASE}/bookings/${data.id}` : `${API_BASE}/bookings`;
          const res = await fetch(url, { method, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) });
          if (res.ok) { setIsEditorOpen(false); onRefresh(); }
      } finally { setActionLoading(false); }
  };

  const handleQuickAdd = (date, time) => {
      setEditingBooking(null);
      setQuickCreateData({ date, time });
      setIsEditorOpen(true);
  };

  // --- Composant Grille Horaire (Jour / Semaine) ---
  const TimeGrid = ({ dates }) => {
    const hours = Array.from({ length: 14 }, (_, i) => 8 + i); // 8h à 21h
    const hourHeight = 80;

    return (
        <div className="bg-white rounded-3xl border border-slate-300 shadow-sm overflow-hidden flex flex-col h-[700px]">
            {/* Header des colonnes */}
            <div className="flex border-b border-slate-300 bg-slate-50/50 sticky top-0 z-20">
                <div className="w-20 border-r border-slate-300"></div>
                {dates.map(dateStr => {
                    const d = new Date(dateStr);
                    const isToday = dateStr === new Date().toISOString().split('T')[0];
                    return (
                        <div key={dateStr} className={`flex-1 py-4 text-center border-r border-slate-300 last:border-0 ${isToday ? 'bg-violet-50/50' : ''}`}>
                            <p className={`text-[10px] font-black uppercase tracking-widest ${isToday ? 'text-violet-600' : 'text-slate-400'}`}>{d.toLocaleDateString(lang, { weekday: 'short' })}</p>
                            <p className={`text-xl font-black ${isToday ? 'text-violet-700' : 'text-slate-800'}`}>{d.getDate()}</p>
                        </div>
                    );
                })}
            </div>

            {/* Corps de la grille avec scroll */}
            <div className="flex-1 overflow-y-auto custom-scrollbar relative bg-white" ref={calendarRef}>
                {/* Lignes d'heures labels fixes à gauche */}
                {hours.map(h => (
                    <div key={h} className="flex border-t border-slate-300 group relative" style={{ height: hourHeight }}>
                        <div className="w-20 relative bg-white z-10 border-r border-slate-300">
                            <span className="absolute -top-2 right-4 text-[10px] font-black text-slate-400 uppercase tracking-tighter">
                                {h}:00
                            </span>
                        </div>
                        {dates.map(date => (
                            <div 
                                key={`${date}-${h}`} 
                                onClick={() => handleQuickAdd(date, `${h.toString().padStart(2, '0')}:00`)}
                                className="flex-1 border-r border-slate-200 last:border-0 hover:bg-slate-50 transition-colors cursor-crosshair relative"
                            >
                                <div className="absolute inset-0 opacity-0 hover:opacity-100 flex items-center justify-center pointer-events-none">
                                    <Plus className="h-4 w-4 text-violet-300" />
                                </div>
                            </div>
                        ))}
                    </div>
                ))}

                {/* Calque des événements */}
                <div className="absolute top-0 left-20 right-0 bottom-0 pointer-events-none">
                    {currentViewBookings.map(b => {
                        const dateIdx = dates.indexOf(b.booking_date);
                        if (dateIdx === -1) return null;

                        const [h, m] = b.booking_time.split(':').map(Number);
                        if (h < 8 || h > 21) return null;

                        const top = (h - 8) * hourHeight + (m / 60) * hourHeight;
                        const height = Math.max((b.duration || 60) / 60 * hourHeight - 2, 40);
                        
                        let statusColor = "bg-violet-50 border-violet-500 text-violet-900";
                        if (b.status === 'confirmed') statusColor = "bg-green-50 border-green-500 text-green-900";
                        if (b.status === 'cancelled') statusColor = "bg-slate-100 border-slate-400 opacity-60 text-slate-500";
                        if (b.status === 'no-show') statusColor = "bg-red-50 border-red-500 text-red-900";

                        const columnWidth = 100 / dates.length;

                        return (
                            <div 
                                key={b.id}
                                onClick={(e) => { e.stopPropagation(); setEditingBooking(b); setQuickCreateData(null); setIsEditorOpen(true); }}
                                className={`absolute rounded-xl border-l-[4px] p-3 shadow-md z-10 transition-all hover:scale-[1.02] cursor-pointer overflow-hidden pointer-events-auto ${statusColor}`}
                                style={{ 
                                    top, 
                                    height, 
                                    left: `calc(${dateIdx * columnWidth}% + 4px)`, 
                                    width: `calc(${columnWidth}% - 8px)` 
                                }}
                            >
                                <div className="flex justify-between items-start">
                                    <p className="font-black text-[11px] truncate leading-none">{b.customer_name}</p>
                                    <span className="text-[9px] font-black opacity-40">{b.booking_time}</span>
                                </div>
                                <p className="text-[9px] font-bold mt-1 opacity-70 truncate uppercase">{b.service_type}</p>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
  };

  const renderMonthView = () => {
    const d = new Date(selectedDate);
    const year = d.getFullYear();
    const month = d.getMonth();
    const firstDay = new Date(year, month, 1).getDay();
    const adjFirst = firstDay === 0 ? 6 : firstDay - 1;
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    
    const cells = [];
    for (let i = 0; i < adjFirst; i++) cells.push(null);
    for (let i = 1; i <= daysInMonth; i++) {
        cells.push(`${year}-${String(month+1).padStart(2, '0')}-${String(i).padStart(2, '0')}`);
    }

    return (
        <div className="bg-white rounded-3xl border border-slate-100 shadow-sm overflow-hidden animate-fade-in">
            <div className="grid grid-cols-7 border-b border-slate-300 bg-slate-50/50">
                {['Lun','Mar','Mer','Jeu','Ven','Sam','Dim'].map(day => (
                    <div key={day} className="py-3 text-center text-[10px] font-black text-slate-400 uppercase tracking-widest">{day}</div>
                ))}
            </div>
            <div className="grid grid-cols-7 auto-rows-[120px]">
                {cells.map((dateStr, idx) => {
                    if (!dateStr) return <div key={`empty-${idx}`} className="border-r border-b border-slate-300 bg-slate-50/20 last:border-r-0"></div>;
                    const dayBookings = bookings.filter(b => b.booking_date === dateStr);
                    const isToday = dateStr === new Date().toISOString().split('T')[0];
                    
                    return (
                        <div key={dateStr} className={`border-r border-b border-slate-300 p-2 overflow-hidden hover:bg-slate-50 transition-colors last:border-r-0 group relative cursor-pointer ${isToday ? 'bg-violet-50/30' : ''}`} onClick={() => { setSelectedDate(dateStr); setViewMode('day'); }}>
                            <div className="flex justify-between items-start mb-1">
                                <span className={`text-xs font-black h-6 w-6 flex items-center justify-center rounded-full ${isToday ? 'bg-violet-600 text-white' : 'text-slate-400'}`}>
                                    {dateStr.split('-')[2]}
                                </span>
                                {dayBookings.length > 0 && <span className="text-[9px] font-black text-violet-600 bg-violet-50 px-1.5 py-0.5 rounded-md">{dayBookings.length}</span>}
                            </div>
                            
                            <button 
                                onClick={(e) => { e.stopPropagation(); handleQuickAdd(dateStr, '10:00'); }}
                                className="absolute top-2 right-2 p-1 bg-violet-100 text-violet-600 rounded-md opacity-0 group-hover:opacity-100 transition-opacity hover:bg-violet-600 hover:text-white"
                            >
                                <Plus className="h-3 w-3" />
                            </button>

                            <div className="space-y-1">
                                {dayBookings.slice(0, 3).map(b => (
                                    <div key={b.id} className={`text-[8px] font-black px-1.5 py-0.5 rounded truncate border-l-2 ${b.status === 'confirmed' ? 'bg-green-50 border-green-500 text-violet-700' : 'bg-amber-50 border-amber-500 text-amber-700'}`}>
                                        {b.booking_time} {b.customer_name}
                                    </div>
                                ))}
                                {dayBookings.length > 3 && <p className="text-[8px] text-center font-bold text-slate-300">+{dayBookings.length - 3} autres</p>}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
  };

  return (
    <div className="flex flex-col h-full space-y-6 animate-fade-in pb-12">
      <div className="flex flex-col lg:flex-row justify-between items-center bg-white p-4 rounded-3xl shadow-sm border border-slate-100 gap-4">
        {viewMode !== 'list' ? (
          <div className="flex items-center gap-4">
            <button 
                onClick={() => setSelectedDate(new Date().toISOString().split('T')[0])}
                className="px-5 py-2 bg-slate-100 text-slate-700 rounded-xl text-xs font-black uppercase hover:bg-slate-200 transition-all border border-slate-200"
            >
                {adminT.today || "Aujourd'hui"}
            </button>
            <div className="flex bg-slate-100 rounded-xl p-1 border border-slate-200">
                <button onClick={() => handleNavigate(-1)} className="p-2 hover:bg-white rounded-lg transition-all"><ChevronLeft className="h-4 w-4"/></button>
                <button onClick={() => handleNavigate(1)} className="p-2 hover:bg-white rounded-lg transition-all"><ChevronRight className="h-4 w-4"/></button>
            </div>
            <h2 className="text-xl font-black text-slate-800 capitalize min-w-[150px]">
                {new Date(selectedDate).toLocaleDateString(lang, { 
                    month: 'long', 
                    year: 'numeric',
                    ...(viewMode === 'day' ? { day: 'numeric' } : {}) 
                })}
            </h2>
            <button 
                onClick={() => handleQuickAdd(selectedDate, '10:00')}
                className="ml-4 p-2 bg-violet-600 text-white rounded-xl hover:bg-violet-700 transition-all flex items-center gap-2 px-4 shadow-md"
            >
                <Plus className="h-4 w-4" />
                <span className="text-[10px] font-black uppercase tracking-widest text-white">{adminT.newBooking || "Nouveau RDV"}</span>
            </button>
          </div>
        ) : (
            <div className="flex items-center gap-4 flex-1">
                <button 
                    onClick={() => handleQuickAdd(new Date().toISOString().split('T')[0], '10:00')}
                    className="p-2 bg-violet-600 text-white rounded-xl hover:bg-violet-700 transition-all flex items-center gap-2 px-4 shadow-md"
                >
                    <Plus className="h-4 w-4" />
                    <span className="text-[10px] font-black uppercase tracking-widest text-white">{adminT.newBooking || "Nouveau RDV"}</span>
                </button>
            </div>
        )}

        <div className="flex items-center gap-3">
            <div className="flex bg-slate-100 rounded-xl p-1 border border-slate-200">
                {[
                    { id: 'day', label: adminT.day || 'Jour' },
                    { id: 'week', label: adminT.week || 'Semaine' },
                    { id: 'month', label: adminT.month || 'Mois' },
                    { id: 'list', label: adminT.list || 'Liste' }
                ].map(view => (
                    <button 
                        key={view.id}
                        onClick={() => setViewMode(view.id)}
                        className={`px-4 py-1.5 rounded-lg text-[10px] font-black uppercase transition-all ${viewMode === view.id ? 'bg-white shadow-sm text-violet-600' : 'text-slate-400 hover:text-slate-600'}`}
                    >
                        {view.label}
                    </button>
                ))}
            </div>
        </div>
      </div>

      <div className="flex-1 min-h-0">
          {viewMode === 'day' && <TimeGrid dates={[selectedDate]} />}
          {viewMode === 'week' && <TimeGrid dates={weekDates} />}
          {viewMode === 'month' && renderMonthView()}
          {viewMode === 'list' && (
              <div className="bg-white rounded-3xl border border-slate-100 shadow-sm overflow-hidden">
                   <div className="p-6 bg-slate-50/50 border-b border-slate-100 flex justify-between items-center">
                        <span className="text-xs font-black text-slate-400 uppercase tracking-widest">Total : {currentViewBookings.length} résultats</span>
                        <div className="flex items-center gap-2">
                             <div className="relative">
                                 <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-300" />
                                 <input 
                                    type="text" 
                                    placeholder="Rechercher..." 
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="pl-10 pr-4 py-2 bg-white border border-slate-200 rounded-xl text-xs focus:ring-2 focus:ring-violet-500 outline-none w-64" 
                                 />
                             </div>
                        </div>
                   </div>
                   <div className="overflow-x-auto">
                        <table className="w-full text-left">
                            <thead className="bg-slate-50 text-[10px] font-black uppercase text-slate-400 border-b border-slate-100">
                                <tr>
                                    <th className="px-6 py-5">{bookT.date} & {bookT.time}</th>
                                    <th className="px-6 py-5">{bookT.customer}</th>
                                    <th className="px-6 py-5">Prestations</th>
                                    <th className="px-6 py-5">{bookT.status}</th>
                                    <th className="px-6 py-5 text-right">{adminT.actions}</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-50">
                                {currentViewBookings.length === 0 ? (
                                    <tr><td colSpan="5" className="px-6 py-20 text-center text-slate-400 italic font-bold">{bookT.noBookings}</td></tr>
                                ) : (
                                    [...currentViewBookings].sort((a,b) => b.booking_date.localeCompare(a.booking_date)).map(b => (
                                        <tr key={b.id} className="hover:bg-slate-50 transition-colors group cursor-pointer" onClick={() => { setEditingBooking(b); setIsEditorOpen(true); }}>
                                            <td className="px-6 py-5">
                                                <div className="flex flex-col">
                                                    <span className="font-bold text-slate-800 text-sm">{new Date(b.booking_date).toLocaleDateString(lang, { day: '2-digit', month: '2-digit', year: 'numeric' })}</span>
                                                    <span className="text-[10px] font-black text-violet-600">{b.booking_time}</span>
                                                </div>
                                            </td>
                                            <td className="px-6 py-5">
                                                <div className="flex items-center gap-3">
                                                    <div className="h-8 w-8 rounded-full bg-slate-100 flex items-center justify-center text-slate-400 font-bold text-xs uppercase">{(b.customer_name || 'C').charAt(0)}</div>
                                                    <span className="font-black text-slate-900 text-sm">{b.customer_name}</span>
                                                </div>
                                            </td>
                                            <td className="px-6 py-5"><span className="text-[10px] font-bold text-slate-500 uppercase">{b.service_type}</span></td>
                                            <td className="px-6 py-5">
                                                <span className={`text-[9px] font-black px-3 py-1 rounded-full uppercase ${
                                                    b.status === 'confirmed' ? 'bg-green-100 text-green-700' : 
                                                    b.status === 'pending' ? 'bg-amber-100 text-amber-700' : 
                                                    'bg-slate-100 text-slate-500'
                                                }`}>
                                                    {b.status}
                                                </span>
                                            </td>
                                            <td className="px-6 py-5 text-right" onClick={e => e.stopPropagation()}>
                                                <div className="flex justify-end gap-1">
                                                    <button onClick={() => { setEditingBooking(b); setIsEditorOpen(true); }} className="p-2 text-slate-400 hover:text-violet-600 transition-all"><Edit2 className="h-4 w-4"/></button>
                                                    <button onClick={() => { if(window.confirm(t.admin.gallery.deleteConfirm)) onDelete(b.id); }} className="p-2 text-slate-300 hover:text-red-600 transition-all"><Trash2 className="h-4 w-4"/></button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                   </div>
              </div>
          )}
      </div>

      <BookingEditorModal 
        isOpen={isEditorOpen} 
        onClose={() => { setIsEditorOpen(false); setEditingBooking(null); setQuickCreateData(null); }} 
        booking={editingBooking} 
        initialData={quickCreateData}
        onSave={handleSaveBooking} 
        loading={actionLoading} 
        services={services} 
        lang={lang} 
        t={t}
      />
    </div>
  );
}
